import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiCheck, FiRefreshCw } from 'react-icons/fi';

const TABS = ['Accounts', 'Transactions', 'Transfers', 'Reconciliation'];

const Banking = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Accounts');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [accountForm, setAccountForm] = useState({ accountName: '', bankName: '', glAccountId: '', currencyCode: '', sortCode: '', accountNumber: '' });
  const [transactionForm, setTransactionForm] = useState({ bankAccountId: '', transactionDate: new Date().toISOString().slice(0, 10), description: '', amount: '' });
  const [transferForm, setTransferForm] = useState({ fromBankAccountId: '', toBankAccountId: '', amount: '', transferDate: new Date().toISOString().slice(0, 10) });
  const [reconForm, setReconForm] = useState({ bankAccountId: '', statementStartDate: '', statementEndDate: '', statementOpeningBalance: '', statementClosingBalance: '' });

  const [categorizingId, setCategorizingId] = useState(null);
  const [categorizeAccountId, setCategorizeAccountId] = useState('');
  const [transactionFilter, setTransactionFilter] = useState('');

  const [selectedRecon, setSelectedRecon] = useState(null);
  const [reconMatches, setReconMatches] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/banking/accounts'),
      apiClient.get('/banking/transactions'),
      apiClient.get('/banking/transfers'),
      apiClient.get('/banking/reconciliations'),
      apiClient.get('/accounting/accounts'),
      apiClient.get('/lookups/currencies'),
    ]).then(([accRes, txnRes, transRes, reconRes, glRes, currRes]) => {
      setAccounts(accRes.data.data);
      setTransactions(txnRes.data.data);
      setTransfers(transRes.data.data);
      setReconciliations(reconRes.data.data);
      setGlAccounts(glRes.data.data);
      setCurrencies(currRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load banking data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const accountName = (id) => accounts.find((a) => a.id === id)?.accountName || id;
  const symbolForCurrencyId = (currencyId) => currencies.find((c) => c.id === currencyId)?.symbol || '£';
  const symbolForAccount = (accountId) => symbolForCurrencyId(accounts.find((a) => a.id === accountId)?.currencyId);

  // ---- Accounts ----
  const handleAddAccount = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...accountForm };
      if (!payload.currencyCode) delete payload.currencyCode;
      if (!payload.sortCode) delete payload.sortCode;
      if (!payload.accountNumber) delete payload.accountNumber;
      await apiClient.post('/banking/accounts', payload);
      success('✅ Bank account added');
      setShowForm(false);
      setAccountForm({ accountName: '', bankName: '', glAccountId: '', currencyCode: '', sortCode: '', accountNumber: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add bank account')); }
  };

  const handleToggleAccountActive = async (accountId, currentlyActive) => {
    try {
      await apiClient.patch(`/banking/accounts/${accountId}`, { isActive: !currentlyActive });
      success(currentlyActive ? '✅ Account deactivated' : '✅ Account reactivated');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update account')); }
  };

  // ---- Transactions ----
  const handleAddTransaction = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/banking/transactions', transactionForm);
      success('✅ Transaction recorded');
      setShowForm(false);
      setTransactionForm({ bankAccountId: '', transactionDate: new Date().toISOString().slice(0, 10), description: '', amount: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not record transaction')); }
  };

  const startCategorize = (txn) => {
    setCategorizingId(txn.id);
    setCategorizeAccountId('');
  };

  const handleCategorize = async (e, transactionId) => {
    e.preventDefault();
    try {
      await apiClient.patch(`/banking/transactions/${transactionId}/categorize`, { accountId: categorizeAccountId });
      success('✅ Transaction categorized');
      setCategorizingId(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not categorize transaction')); }
  };

  const filteredTransactions = transactionFilter
    ? transactions.filter((t) => t.bankAccountId === transactionFilter)
    : transactions;

  // ---- Transfers ----
  const handleAddTransfer = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/banking/transfers', transferForm);
      success('✅ Transfer recorded');
      setShowForm(false);
      setTransferForm({ fromBankAccountId: '', toBankAccountId: '', amount: '', transferDate: new Date().toISOString().slice(0, 10) });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not record transfer')); }
  };

  // ---- Reconciliation ----
  const handleStartRecon = async (e) => {
    e.preventDefault();
    try {
      const { data } = await apiClient.post('/banking/reconciliations', reconForm);
      success('✅ Reconciliation started');
      setShowForm(false);
      setReconForm({ bankAccountId: '', statementStartDate: '', statementEndDate: '', statementOpeningBalance: '', statementClosingBalance: '' });
      loadAll();
      viewRecon(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not start reconciliation')); }
  };

  const viewRecon = async (recon) => {
    setSelectedRecon(recon);
    setReconMatches(null);
    try {
      const { data } = await apiClient.get(`/banking/reconciliations/${recon.id}/matches`);
      setReconMatches(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load matches')); }
  };

  const unmatchedForRecon = () => {
    if (!selectedRecon || !reconMatches) return [];
    const matchedIds = new Set(reconMatches.map((m) => m.bankTransactionId));
    return transactions.filter((t) => t.bankAccountId === selectedRecon.bankAccountId && !matchedIds.has(t.id));
  };

  const handleAddMatch = async (bankTransactionId) => {
    try {
      await apiClient.post(`/banking/reconciliations/${selectedRecon.id}/matches`, { bankTransactionId, matchType: 'manual' });
      success('✅ Transaction matched');
      const { data } = await apiClient.get(`/banking/reconciliations/${selectedRecon.id}/matches`);
      setReconMatches(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not match transaction')); }
  };

  const handleCompleteRecon = async () => {
    if (!window.confirm('Complete this reconciliation? Make sure all statement transactions are matched first.')) return;
    try {
      await apiClient.post(`/banking/reconciliations/${selectedRecon.id}/complete`);
      success('✅ Reconciliation completed');
      setSelectedRecon(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not complete reconciliation')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Banking</h1>
        <p>Bank accounts, transactions, transfers, and reconciliation</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); setSelectedRecon(null); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Accounts' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Bank Accounts</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Account'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddAccount} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Account Name *</label><input required value={accountForm.accountName} onChange={(e) => setAccountForm(p => ({ ...p, accountName: e.target.value }))} placeholder="Main Current Account" /></div>
              <div className="form-field"><label>Bank Name</label><input value={accountForm.bankName} onChange={(e) => setAccountForm(p => ({ ...p, bankName: e.target.value }))} /></div>
              <div className="form-field">
                <label>GL Account *</label>
                <select required value={accountForm.glAccountId} onChange={(e) => setAccountForm(p => ({ ...p, glAccountId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {glAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Currency</label>
                <select value={accountForm.currencyCode} onChange={(e) => setAccountForm(p => ({ ...p, currencyCode: e.target.value }))}>
                  <option value="">Organisation default</option>
                  {currencies.map((c) => <option key={c.id} value={c.code}>{c.code} ({c.symbol}) — {c.name}</option>)}
                </select>
              </div>
              <div className="form-field"><label>Sort Code</label><input value={accountForm.sortCode} onChange={(e) => setAccountForm(p => ({ ...p, sortCode: e.target.value }))} placeholder="12-34-56" /></div>
              <div className="form-field"><label>Account Number</label><input value={accountForm.accountNumber} onChange={(e) => setAccountForm(p => ({ ...p, accountNumber: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Account</th><th>Bank</th><th>Currency</th><th>Balance</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No bank accounts yet</td></tr>
                ) : accounts.map((a) => (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 600 }}>{a.accountName}</td>
                    <td>{a.bankName || '—'}</td>
                    <td>{symbolForCurrencyId(a.currencyId)} {currencies.find(c => c.id === a.currencyId)?.code || ''}</td>
                    <td>{symbolForCurrencyId(a.currencyId)}{Number(a.currentBalance || 0).toFixed(2)}</td>
                    <td><span className="badge" style={{ background: a.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{a.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleToggleAccountActive(a.id, a.isActive)}>
                        {a.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Transactions' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Transactions</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Transaction'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddTransaction} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field">
                <label>Bank Account *</label>
                <select required value={transactionForm.bankAccountId} onChange={(e) => setTransactionForm(p => ({ ...p, bankAccountId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.accountName}</option>)}
                </select>
              </div>
              <div className="form-field"><label>Date *</label><input type="date" required value={transactionForm.transactionDate} onChange={(e) => setTransactionForm(p => ({ ...p, transactionDate: e.target.value }))} /></div>
              <div className="form-field full-width"><label>Description</label><input value={transactionForm.description} onChange={(e) => setTransactionForm(p => ({ ...p, description: e.target.value }))} /></div>
              <div className="form-field">
                <label>Amount {transactionForm.bankAccountId ? `(${symbolForAccount(transactionForm.bankAccountId)})` : ''} *</label>
                <input required type="number" step="0.01" value={transactionForm.amount} onChange={(e) => setTransactionForm(p => ({ ...p, amount: e.target.value }))} placeholder="Positive = in, negative = out" />
              </div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="form-field" style={{ maxWidth: '300px' }}>
            <label>Filter by account</label>
            <select value={transactionFilter} onChange={(e) => setTransactionFilter(e.target.value)}>
              <option value="">All accounts</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.accountName}</option>)}
            </select>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Date</th><th>Account</th><th>Description</th><th>Amount</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No transactions yet</td></tr>
                ) : filteredTransactions.map((t) => (
                  <tr key={t.id}>
                    <td>{new Date(t.transactionDate).toLocaleDateString()}</td>
                    <td>{accountName(t.bankAccountId)}</td>
                    <td>{t.description || '—'}</td>
                    <td style={{ fontWeight: 600, color: Number(t.amount) >= 0 ? '#10b981' : '#ef4444' }}>
                      {symbolForCurrencyId(t.currencyId)}{Number(t.amount).toFixed(2)}
                    </td>
                    <td>
                      <span className="badge" style={{ background: t.reconciliationStatus === 'matched' ? '#10b981' : t.categoryLookupId ? '#3b82f6' : '#f59e0b', color: 'white' }}>
                        {t.reconciliationStatus === 'matched' ? 'Matched' : t.categoryLookupId ? 'Categorized' : 'Uncategorized'}
                      </span>
                    </td>
                    <td>
                      {categorizingId === t.id ? (
                        <form onSubmit={(e) => handleCategorize(e, t.id)} style={{ display: 'flex', gap: '0.4rem' }}>
                          <select required value={categorizeAccountId} onChange={(e) => setCategorizeAccountId(e.target.value)} style={{ padding: '0.3rem' }}>
                            <option value="">-- GL account --</option>
                            {glAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                          </select>
                          <button type="submit" className="btn btn-sm btn-primary" style={{ width: 'auto' }}>Save</button>
                          <button type="button" className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setCategorizingId(null)}>Cancel</button>
                        </form>
                      ) : (
                        <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => startCategorize(t)}>
                          Categorize
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Transfers' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Transfers Between Accounts</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Transfer'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddTransfer} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field">
                <label>From Account *</label>
                <select required value={transferForm.fromBankAccountId} onChange={(e) => setTransferForm(p => ({ ...p, fromBankAccountId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.accountName}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>To Account *</label>
                <select required value={transferForm.toBankAccountId} onChange={(e) => setTransferForm(p => ({ ...p, toBankAccountId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {accounts.filter((a) => a.id !== transferForm.fromBankAccountId).map((a) => <option key={a.id} value={a.id}>{a.accountName}</option>)}
                </select>
              </div>
              <div className="form-field"><label>Amount *</label><input required type="number" step="0.01" value={transferForm.amount} onChange={(e) => setTransferForm(p => ({ ...p, amount: e.target.value }))} /></div>
              <div className="form-field"><label>Date *</label><input type="date" required value={transferForm.transferDate} onChange={(e) => setTransferForm(p => ({ ...p, transferDate: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Transfer</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Date</th><th>From</th><th>To</th><th>Amount</th></tr></thead>
              <tbody>
                {transfers.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No transfers yet</td></tr>
                ) : transfers.map((t) => (
                  <tr key={t.id}>
                    <td>{new Date(t.transferDate).toLocaleDateString()}</td>
                    <td>{accountName(t.fromBankAccountId)}</td>
                    <td>{accountName(t.toBankAccountId)}</td>
                    <td style={{ fontWeight: 600 }}>{Number(t.amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Reconciliation' && (
        <>
          <section className="page-section">
            <div className="section-header">
              <h2>Reconciliations</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'Start Reconciliation'}
              </button>
            </div>
            {showForm && (
              <form onSubmit={handleStartRecon} className="form-grid" style={{ marginBottom: '1.5rem' }}>
                <div className="form-field">
                  <label>Bank Account *</label>
                  <select required value={reconForm.bankAccountId} onChange={(e) => setReconForm(p => ({ ...p, bankAccountId: e.target.value }))}>
                    <option value="">-- select --</option>
                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.accountName}</option>)}
                  </select>
                </div>
                <div className="form-field"><label>Statement Start *</label><input type="date" required value={reconForm.statementStartDate} onChange={(e) => setReconForm(p => ({ ...p, statementStartDate: e.target.value }))} /></div>
                <div className="form-field"><label>Statement End *</label><input type="date" required value={reconForm.statementEndDate} onChange={(e) => setReconForm(p => ({ ...p, statementEndDate: e.target.value }))} /></div>
                <div className="form-field"><label>Opening Balance *</label><input required type="number" step="0.01" value={reconForm.statementOpeningBalance} onChange={(e) => setReconForm(p => ({ ...p, statementOpeningBalance: e.target.value }))} /></div>
                <div className="form-field"><label>Closing Balance *</label><input required type="number" step="0.01" value={reconForm.statementClosingBalance} onChange={(e) => setReconForm(p => ({ ...p, statementClosingBalance: e.target.value }))} /></div>
                <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Start</button></div>
              </form>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Account</th><th>Period</th><th>Closing Balance</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {reconciliations.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No reconciliations yet</td></tr>
                  ) : reconciliations.map((r) => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600 }}>{accountName(r.bankAccountId)}</td>
                      <td>{new Date(r.statementStartDate).toLocaleDateString()} – {new Date(r.statementEndDate).toLocaleDateString()}</td>
                      <td>{symbolForAccount(r.bankAccountId)}{Number(r.statementClosingBalance).toFixed(2)}</td>
                      <td><span className="badge" style={{ background: r.status === 'completed' ? '#10b981' : '#f59e0b', color: 'white' }}>{r.status}</span></td>
                      <td><button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewRecon(r)}>Open</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {selectedRecon && (
            <section className="page-section">
              <div className="section-header">
                <h2>Reconciling {accountName(selectedRecon.bankAccountId)}</h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {selectedRecon.status !== 'completed' && (
                    <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={handleCompleteRecon}>
                      <FiCheck size={14} /> Complete
                    </button>
                  )}
                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedRecon(null)}>Close</button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div>
                  <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Matched</h3>
                  {reconMatches === null ? <p style={{ color: '#6b7280' }}>Loading...</p> : reconMatches.length === 0 ? (
                    <p style={{ color: '#6b7280' }}>Nothing matched yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {reconMatches.map((m) => {
                        const txn = transactions.find((t) => t.id === m.bankTransactionId);
                        return (
                          <div key={m.id} style={{ padding: '0.6rem', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0', fontSize: '0.85rem' }}>
                            {txn ? `${new Date(txn.transactionDate).toLocaleDateString()} — ${txn.description || 'No description'} — ${symbolForCurrencyId(txn.currencyId)}${Number(txn.amount).toFixed(2)}` : m.bankTransactionId}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Unmatched (this account)</h3>
                  {unmatchedForRecon().length === 0 ? (
                    <p style={{ color: '#6b7280' }}>Nothing left to match.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {unmatchedForRecon().map((t) => (
                        <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '0.85rem' }}>
                          <span>{new Date(t.transactionDate).toLocaleDateString()} — {t.description || 'No description'} — {symbolForCurrencyId(t.currencyId)}{Number(t.amount).toFixed(2)}</span>
                          <button className="action-icon edit" title="Match" onClick={() => handleAddMatch(t.id)}>
                            <FiRefreshCw size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </motion.div>
  );
};

export default Banking;